import React, { SetStateAction, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { ProductService } from '@services';
import { useServiceRef } from 'hooks';
import { Price, ProductDetails, Product, Variant } from '@services/product/interfaces';
import { ProductOptions } from 'components/ProductOptions';
import { AddToCartButton } from 'components/AddToCartButton';
import { Counter } from 'gd-design-library';
import styles from './QuickCartControls.module.scss';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { QuickCartControlsSkeleton } from './QuickCartControls.skeleton';
import { pushAddToCartEvent } from '@utils';
import { Button } from 'components/baseComponents/Button';

const CN = 'quick-cart-controls';
const attributesClassName = `${CN}__attributes`;
const quantityLabelClassName = `${CN}__quantity-label`;
const addToCartButtonClassName = `${CN}__add-to-cart-button`;
const cx = classnames.bind(styles);

type Availability = Record<string, { values: Record<string, boolean>}>

interface QuickCartControlsProps {
  selectedVariant: Variant;
  setSelectedVariant: (state: SetStateAction<Variant>) => void;
  product: Product;
  isOpen: boolean;
  handleAddToCart: (lineItemDraft: LineItemDraft, currency?: string) => Promise<void>;
  matchingVariantId?: number;
  className?: string;
}

export const QuickCartControls: React.FC<QuickCartControlsProps> = ({
  selectedVariant,
  setSelectedVariant,
  product,
  isOpen,
  handleAddToCart,
  matchingVariantId,
  className
}) => {
  const [ productDetails, setProductDetails ] = useState<ProductDetails | null>(null);
  const productServiceRef = useServiceRef(ProductService);
  const [ quantityValue, setQuantityValue ] = useState(1);
  const [ isAddingToCart, setIsAddingToCart ] = useState(false);
  const [ showAllAttributes, setShowAllAttributes ] = useState(false);
  const [ availableQuantity, setAvailableQuantity ] = useState<number | null>(null);

  const {
    attributes: selectedVariantAttributes,
    price: selectedVariantPrice
  } = selectedVariant;

  const currentPrice: Price = selectedVariantPrice?.value ? selectedVariantPrice : product.price;

  const onClickOption = (optionName: string, optionValue: unknown): void => {
    if (productDetails) {
      const userSelectedVariant = { ...selectedVariantAttributes, [optionName]: optionValue };
      const foundVariant =
      productDetails
        .variants.find(variant => JSON.stringify(variant.attributes) === JSON.stringify(userSelectedVariant));
      if (foundVariant) {
        setSelectedVariant({ ...foundVariant });
      } else {
        const anotherVariant = productDetails
          .variants.find(variant => variant.attributes[optionName] === optionValue);
        if (anotherVariant) {
          setSelectedVariant(anotherVariant);
        }
      }
    }
  };

  const onAddToCart = async (): Promise<void> => {
    setIsAddingToCart(true);
    const lineItemDraft = {
      productId: product.id,
      variantId: selectedVariant.id,
      quantity: quantityValue
    };
    await handleAddToCart(lineItemDraft, currentPrice.currencyCode);
    pushAddToCartEvent(product, quantityValue);
    setIsAddingToCart(false);
  };

  const optionsAvailability = useMemo(() => {
    const variantSet = productDetails
      ?.variants.reduce((acc, variant) => acc.add(JSON.stringify(variant.attributes)), new Set());
    const calculatedOptionsAvailability: Availability = {};

    if (productDetails) {
      productDetails.options.forEach(option => {
        calculatedOptionsAvailability[option.name] = { values: {} };

        option.values?.forEach(value => {
          calculatedOptionsAvailability[option.name].values[String(value)] = false;
          const newVariant = { ...selectedVariantAttributes };
          newVariant[option.name] = value;

          if (variantSet && variantSet.has(JSON.stringify(newVariant))) {
            calculatedOptionsAvailability[option.name].values[String(value)] = true;
          }
        });
      });
    }

    return calculatedOptionsAvailability;
  }, [ selectedVariantAttributes, productDetails ]);

  const outOfStockOptions = useMemo(() => {
    if (!productDetails) return {};

    const result: Record<string, Record<string, boolean>> = {};

    for (const option of productDetails.options) {
      result[option.name] = {};

      if (option.values) {
        for (const value of option.values) {
          const attributes = { ...selectedVariantAttributes, [option.name]: value };
          const variant = productDetails.variants.find(v =>
            Object.entries(attributes).every(([ k, vAttr ]) => v.attributes[k] === vAttr)
          );
          result[option.name][String(value)] = true;
          if (variant && variant.isOnStock) {
            result[option.name][String(value)] = false;
          }
        }
      }
    }

    return result;
  }, [ selectedVariantAttributes, productDetails ]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (isOpen) {
        if (!productDetails) {
          const productDetailsForQuickView = await productServiceRef.current.getProductData(product.id);
          setProductDetails(productDetailsForQuickView);

          if (productDetailsForQuickView?.variants?.length) {
            const matchingVariant = productDetailsForQuickView?.variants.find(({ id }) => id === matchingVariantId);

            const firstAvailableVariant = productDetailsForQuickView.variants.find(variant => variant.isOnStock === true);
            setSelectedVariant(matchingVariant ?? firstAvailableVariant ?? productDetailsForQuickView.variants[0]);
          }
        }
      }
    })();
  }, [ isOpen ]);

  useEffect(() => {
    if (!isOpen && showAllAttributes) {
      setShowAllAttributes(false);
    }
  }, [ isOpen ]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (selectedVariant?.sku) {
        const quantity = await productServiceRef.current.getAvailableQuantity(selectedVariant.sku);
        setAvailableQuantity(quantity);
      } else {
        setAvailableQuantity(null);
      }
    })();
  }, [ selectedVariant?.sku ]);

  // TODO: Remove this once the option is removed from the sokos products or sokos tenant is removed
  const options = productDetails?.options.filter(option => option.name !== 'sokos-sokos_product-purchase_limit');

  return isOpen ? (
    <div className={ cx(CN, className) }>
      { productDetails ? (
        <>
          <div className={ cx(attributesClassName) }>
            { (showAllAttributes ? options : options.slice(0, 3)).map(currentOptions => (
              <div key={ currentOptions.name }>
                <ProductOptions
                  options={ currentOptions }
                  selectedOption={ selectedVariantAttributes?.[currentOptions.name] }
                  availability={ optionsAvailability[currentOptions.name]?.values }
                  outOfStockOptions={ outOfStockOptions[currentOptions.name] }
                  onClickOption={ onClickOption }
                  variants={ productDetails?.variants }
                  quickCart={ true }
                />
              </div>
            ))}
            { options.length > 3 && !showAllAttributes && (
              <Button
                variant='inherit'
                onClick={ (): void => setShowAllAttributes(!showAllAttributes) }
              >
                Show More
              </Button>
            )}
          </div>
          <div className={ cx(quantityLabelClassName) }>
            Quantity:
          </div>
          <Counter
            initial={ quantityValue }
            min={ 1 }
            max={ availableQuantity ?? (!selectedVariant.isOnStock ? 1 : undefined) }
            isDisabled={ !selectedVariant.isOnStock }
            onCounterChange={ setQuantityValue }
          />
          <AddToCartButton
            onClick={ onAddToCart }
            isLoading={ isAddingToCart }
            isOnStock={ selectedVariant.isOnStock }
            disabled={ selectedVariant.price ? selectedVariant.price.value === undefined : currentPrice.value === undefined }
            quickCart={ true }
            className={ cx(addToCartButtonClassName) }
          />
        </>
      ) : <QuickCartControlsSkeleton />}
    </div>
  ) : null;
};
